import { createContext, useContext, useEffect, useSyncExternalStore } from "react";

/**
 * Country-root SSR bootstrap (country-page approach review, 2026-10-04).
 *
 * A country root (`/dk`, `/uk/en`, ...) is rendered on the server by the very
 * same React tree the browser runs, then hydrated. For the two renders to be
 * identical the first render must be a deterministic, anonymous snapshot that
 * depends only on the URL and the frozen inventory seed: no storage reads, no
 * clocks, no session. The server marks this by providing `SsrRequestContext`;
 * the browser detects the marker attribute the server wrote on `#root`.
 * Once the tree has hydrated, `endSsrBootstrap()` switches providers back to
 * their normal behaviour (storage-backed preferences are applied in effects).
 */
export const COUNTRY_ROOT_ATTR = "data-bac-ssr-country-root";
export const PUBLIC_PAGE_ATTR = "data-bac-ssr-public-page";

/** Shared document state, independent of the protected US homepage seed. */
interface SsrDocumentState {
  /** Server decided this public document is a 404; render the branded page. */
  notFound?: boolean;
}

export interface SsrRequest extends SsrDocumentState {
  canonical?: string;
  /** URL pathname exactly as requested (already normalised by the edge). */
  pathname: string;
  /** Frozen same-country inventory response, shared with the client seed. */
  seed?: unknown;
  flags?: Record<string, boolean>;
  /** Request-owned metadata emitted by the same component as the body. */
  pageMeta?: import("./seo").PageMeta;
}

/** Per-request value; never module state, so renders cannot leak. */
export const SsrRequestContext = createContext<SsrRequest | null>(null);

/** `#root` attribute carrying the pathname the server rendered as a 404. */
export const NOT_FOUND_ATTR = "data-bac-ssr-not-found";

/**
 * True only for the exact request the server rendered as a 404 (SSR context
 * flag), or on the client while the address bar still equals the path the
 * server stamped on `#root`. Any navigation makes it false again.
 */
export function isSsrNotFound(request: SsrRequest | null, pathname: string): boolean {
  if (request) return !!request.notFound;
  if (typeof document === "undefined") return false;
  const stamped = document.getElementById("root")?.getAttribute(NOT_FOUND_ATTR);
  return !!stamped && stamped === pathname;
}

let clientBootstrap =
  typeof document !== "undefined" &&
  !!(document.getElementById("root")?.hasAttribute(COUNTRY_ROOT_ATTR) ||
    document.getElementById("root")?.hasAttribute(PUBLIC_PAGE_ATTR));

export function isClientSsrBootstrap(): boolean {
  return clientBootstrap;
}

export function endSsrBootstrap(): void {
  clientBootstrap = false;
}

let publicPageHydrated = false;
const publicHydrationListeners = new Set<() => void>();
export function subscribePublicHydration(listener: () => void): () => void {
  publicHydrationListeners.add(listener);
  return () => { publicHydrationListeners.delete(listener); };
}
export function isPublicPageHydrated(): boolean {
  return publicPageHydrated;
}
/** Keep the emitted authority after startup, but never reuse it on another route. */
export function readPublicLandingCanonical(): string | undefined {
  if (typeof document === "undefined" || typeof window === "undefined" ||
    !document.getElementById("root")?.hasAttribute(PUBLIC_PAGE_ATTR)) return;
  try {
    const snapshot = JSON.parse(document.getElementById("public-page-ssr-data")?.textContent || "");
    if (snapshot.pathname !== window.location.pathname || snapshot.seed?.kind !== "landing") return;
    const value = snapshot.seed.currentCanonical;
    return typeof value === "string" && new URL(value).protocol === "https:" ? value : undefined;
  } catch { return; }
}
let publicQueries: { queryKey: unknown[]; state: { data: unknown; dataUpdatedAt: number } }[] | undefined;
/** Only the anonymous public document, never a previous account's query cache. */
export function readPublicSsrQuerySeed<T>(queryKey: unknown[]): { data: T; updatedAt: number } | undefined {
  if (typeof document === "undefined" || !document.getElementById("root")?.hasAttribute(PUBLIC_PAGE_ATTR)) return;
  if (!publicQueries) {
    const source = document.getElementById("public-page-ssr-data")?.textContent;
    if (!source) throw new Error("Public SSR query snapshot missing");
    publicQueries = JSON.parse(source).dehydratedState?.queries;
    if (!Array.isArray(publicQueries)) throw new Error("Public SSR query snapshot invalid");
  }
  const hash = JSON.stringify(queryKey);
  const match = publicQueries.find(query => JSON.stringify(query.queryKey) === hash);
  return match ? { data: match.state.data as T, updatedAt: match.state.dataUpdatedAt } : undefined;
}
/** A parent can commit while its lazy public page still awaits hydration. */
export function usePublicPageHydrationReady(ready: boolean): void {
  const request = useContext(SsrRequestContext);
  useEffect(() => {
    if (!request?.flags || !ready || publicPageHydrated) return;
    publicPageHydrated = true;
    endSsrBootstrap();
    for (const listener of publicHydrationListeners) listener();
  }, [request, ready]);
}

/** True while rendering the server document or hydrating it in the browser. */
const subscribeHydration = () => () => {};
const clientHydrationSnapshot = () => false;
const serverHydrationSnapshot = () => true;

export function useSsrBootstrap(): boolean {
  const request = useContext(SsrRequestContext);
  // A root effect can commit before a Suspense boundary is selectively
  // hydrated. React keeps the server snapshot for each component's hydration
  // render, then switches it to the client snapshot after that component
  // commits. A mutable module flag cannot provide that guarantee.
  const hydrating = useSyncExternalStore(
    subscribeHydration,
    clientHydrationSnapshot,
    serverHydrationSnapshot,
  );
  return !!request || hydrating;
}
